import {useMemo, useRef, useState} from 'react';
import {capitalize} from '@utils';
import {checkinName} from '@utils/checkin-name';
import {useStoreContext} from '@store/store-context';
import {MemberProfileRequestImpl} from '../../member-profile-request';
import {MemberProfileController} from '../../member-profile-controller';
import {
  createNotificationPreferencesDraft,
  NotificationChannel,
  NotificationPreferencesDraft,
  notificationPreferencesAreEqual,
  toggleNotificationParameter,
} from '../notification-preferences';

export const useNotification = () => {
  const {
    authStore: {auth, setInitResponseAsAuth, setInitResponse},
  } = useStoreContext();

  const initialDraft = useMemo(
    () => createNotificationPreferencesDraft(auth?.user?.notifications),
    [auth?.user?.notifications],
  );
  const [savedDraft, setSavedDraft] = useState(initialDraft);
  const [draft, setDraft] = useState(initialDraft);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [hasSaved, setHasSaved] = useState(false);
  const isSavingRef = useRef(false);

  const checkinSubmissionParameter = `${capitalize(checkinName())} submissions`;

  const savePreferences = async (nextDraft: NotificationPreferencesDraft) => {
    if (
      isSavingRef.current ||
      notificationPreferencesAreEqual(nextDraft, savedDraft)
    )
      return;

    isSavingRef.current = true;
    setDraft(nextDraft);
    setIsSaving(true);
    setSaveError('');
    setHasSaved(false);

    try {
      const request = new MemberProfileRequestImpl();
      const controller = new MemberProfileController(request);
      const response: any = await controller.newMemberProfileForm(
        {notifications: nextDraft},
        auth.tokens.access.token,
        true,
      );

      if (!response) throw new Error('Notification preferences were not saved');

      const storedAuthObject = JSON.parse(
        localStorage.getItem('auth') || JSON.stringify(auth),
      );
      const persistedNotifications = response?.notifications || nextDraft;
      const updatedAuthObject = {
        ...storedAuthObject,
        user: {
          ...storedAuthObject.user,
          notifications: persistedNotifications,
        },
      };

      localStorage.setItem('auth', JSON.stringify(updatedAuthObject));
      setInitResponse(updatedAuthObject);
      setInitResponseAsAuth();

      const persistedDraft = createNotificationPreferencesDraft(
        persistedNotifications,
      );
      setDraft(persistedDraft);
      setSavedDraft(persistedDraft);
      setHasSaved(true);
    } catch {
      setDraft(savedDraft);
      setSaveError(
        'Couldn’t save. Your previous setting was restored. Try again.',
      );
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
    }
  };

  const toggleChannel = (channel: NotificationChannel) => {
    if (isSavingRef.current) return;
    void savePreferences({
      ...draft,
      [channel]: {
        ...draft[channel],
        enabled: !draft[channel].enabled,
      },
    });
  };

  const toggleParameter = (channel: NotificationChannel, parameter: string) => {
    if (isSavingRef.current) return;
    void savePreferences({
      ...draft,
      [channel]: {
        ...draft[channel],
        parameters: toggleNotificationParameter(
          draft[channel].parameters,
          parameter,
        ),
      },
    });
  };

  const workspaceSettings = auth?.user?.workspace?.settings || {};

  return {
    draft,
    isAdmin: auth?.user?.role === 'admin',
    isCheckinsEnabled: workspaceSettings.checkInStatus === 'enabled',
    isGoalsEnabled: workspaceSettings.goalStatus === 'enabled',
    isSaving,
    saveError,
    hasSaved,
    checkinSubmissionParameter,
    toggleChannel,
    toggleParameter,
  };
};
